import React, {
  createContext, 
  useCallback,
  useContext, 
  useEffect,
  useRef,
  useState, 
} from 'react';
import { useHistory } from 'react-router';
import { jwtDecode } from 'jwt-decode';

import useCookie from '../Helpers/cookies';
import { login as apiLogin } from '../Helpers/dataApi';
import { clearSimulation } from '../Helpers/localStorage';

export const authContext = createContext();

export function ProvideAuth({ children }) {
  const auth = useProvideAuth();
  return (
    <authContext.Provider 
      value={auth}
    >
        {children}
    </authContext.Provider>
  );
}

export const useAuth = () => {
  return useContext(authContext);
};

function useProvideAuth() {
  const history = useHistory();
  const devMode = import.meta.env.VITE_DEVELOPMENT_MODE;
  const [activeUser, update, remove] = useCookie('activeUser', false);
  const [loginFailure, setLoginFailure] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const sessionTimerRef = useRef(null);

  // Stable refs so timer callbacks always call the current function,
  // never a stale closure captured at scheduling time.
  const removeRef = useRef(remove);
  const historyRef = useRef(history);
  useEffect(() => { removeRef.current = remove; }, [remove]);
  useEffect(() => { historyRef.current = history; }, [history]);

  // Clear any pending session expiry timer
  const clearSessionTimer = useCallback(() => {
    if (sessionTimerRef.current) {
      clearTimeout(sessionTimerRef.current);
      sessionTimerRef.current = null;
    }
  }, []);

  // Schedule a redirect to /login?reason=session_expired when the JWT expires.
  // Uses refs inside the timer callback so it always sees the latest remove/history,
  // regardless of when the closure was captured.
  const scheduleSessionExpiry = useCallback((exp) => {
    clearSessionTimer();
    const msUntilExpiry = exp * 1000 - Date.now();
    if (msUntilExpiry <= 0) return;
    sessionTimerRef.current = setTimeout(() => {
      removeRef.current('activeUser');
      clearSimulation();
      historyRef.current.replace('/login?reason=session_expired');
    }, msUntilExpiry);
  }, [clearSessionTimer]);

  useEffect(() => {
    if (activeUser) {
      const decodedToken = jwtDecode(activeUser.accessToken);
      if (decodedToken && decodedToken.exp < (Date.now() / 1000)) {
        // Token already expired — clear and redirect immediately
        remove('activeUser');
        clearSimulation();
        history.replace('/login?reason=session_expired');
      } else {
        setIsLoggedIn(true);
        if (decodedToken?.exp) {
          scheduleSessionExpiry(decodedToken.exp);
        }
      }
    } else {
      // User logged out or cookie cleared — cancel any pending timer
      clearSessionTimer();
      setIsLoggedIn(false);
    }

    return () => clearSessionTimer();
  }, [activeUser, clearSessionTimer, scheduleSessionExpiry, history, remove]);

  // Check token validity when the user returns to the tab.
  // Browsers throttle setTimeout in inactive tabs, so the expiry timer may not
  // have fired yet when the user switches back. This catches that gap immediately
  // on tab focus — before any render or interaction — so the user never sees a
  // stale logged-in page.
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;
      if (!activeUser) return;
      try {
        const decodedToken = jwtDecode(activeUser.accessToken);
        if (decodedToken && decodedToken.exp < (Date.now() / 1000)) {
          removeRef.current('activeUser');
          clearSimulation();
          historyRef.current.replace('/login?reason=session_expired');
        }
      } catch {
        // Malformed token — treat as expired
        removeRef.current('activeUser');
        clearSimulation();
        historyRef.current.replace('/login?reason=session_expired');
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [activeUser]);
  

  const login = async (email, password, rememberMe) => {
    
    return await apiLogin(
      email, 
      password,
    ).then((response) => {
      // apiLogin returns the error object on failure (Axios catch returns error).
      // Guard against non-2xx responses before accessing response.data.
      if (!response || !response.data || response instanceof Error || response.isAxiosError) {
        setLoginFailure(true);
        return false;
      }
      if(response.data.access_token && response.data.access_token.length > 0){
        const accessToken = response.data.access_token;
        const userInfo = response.data.user;
        const newActiveUser = {
          accessToken: accessToken,
          email: userInfo.email,
          firstName: userInfo.first_name,
          firstTimeSignIn: userInfo.first_time_sign_in,
          id: userInfo.id,
          lastName: userInfo.last_name,
          organization: userInfo.organization,
          projects: userInfo?.projects,
          userType: userInfo.user_type,
        };
        update('activeUser', newActiveUser);
        setIsLoggedIn(true);
        return true;
      }
      else{
        setLoginFailure(true);
        return false;
      }
    });
  };
  const logout = () => {
    clearSessionTimer();
    setIsLoggedIn(false);
    remove('activeUser');
    clearSimulation(); // Clear simulation state on logout
    // Note: activeProjectId cookie is intentionally preserved to maintain project selection across login sessions
  };
  const sendPasswordResetEmail = (email) => {
    // return firebase
    //   .auth()
    //   .sendPasswordResetEmail(email)
    //   .then(() => {
    //     return true;
    //   });
  };
  const confirmPasswordReset = (code, password) => {
    // return firebase
    //   .auth()
    //   .confirmPasswordReset(code, password)
    //   .then(() => {
    //     return true;
    //   });
  };

  /**
   * Update a subset of activeUser fields in the cookie and trigger a re-render.
   * Used after in-app edits (e.g. profile name change) so the UI reflects the
   * new values immediately without requiring a logout/login cycle.
   */
  const updateActiveUser = useCallback((patch) => {
    if (!activeUser) return;
    const updatedUser = { ...activeUser, ...patch };
    update('activeUser', updatedUser);
  }, [activeUser, update]);

  // Return the user object and auth methods
  return {
    activeUser,
    confirmPasswordReset,
    login,
    loginFailure,
    logout,
    sendPasswordResetEmail,
    updateActiveUser,
  };
}